import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface AdminLayoutProps {
  children: ReactNode;
}

const ADMIN_NAV = [
  { icon: '📊', label: 'Dashboard', href: '/admin' },
  { icon: '⛪', label: 'Churches', href: '/admin/churches' },
];

export function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const pathname = location.pathname;

  return (
    <div className="min-h-screen bg-[var(--color-bg)]">
      {/* Admin sidebar — desktop only */}
      <aside className="hidden md:flex fixed left-0 top-0 h-screen w-64 flex-col bg-[var(--color-bg)] border-r border-[var(--color-border)] z-30">
        <div className="px-5 py-6 border-b border-[var(--color-border)]">
          <h2 className="font-display text-h2 font-bold text-text-primary">
            Admin Panel
          </h2>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {ADMIN_NAV.map((item) => {
            const isActive =
              item.href === '/admin'
                ? pathname === '/admin'
                : pathname.startsWith(item.href);
            const activeClasses = isActive
              ? 'text-[var(--color-primary)] bg-[var(--color-gray-50)] font-semibold'
              : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-gray-50)] hover:text-[var(--color-text)]';

            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg)] focus-visible:outline-none ${activeClasses}`}
              >
                <span className="text-lg">{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-[var(--color-border)]">
          <Link
            to="/dashboard"
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-gray-50)] hover:text-[var(--color-text)] transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg)] focus-visible:outline-none"
          >
            <span className="text-lg">← </span>
            <span>Back to App</span>
          </Link>
        </div>
      </aside>

      {/* Content area */}
      <div className="md:ml-64 min-h-screen">
        <main className="px-4 md:px-6 lg:px-8 pt-6 md:pt-8 pb-6">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
